import React, { useState } from "react";

import { Row, Col, Button, Modal, ModalHeader, ModalBody, ModalFooter, Input } from "reactstrap";
import ReactTags from "react-tag-autocomplete";
import { toastr } from "react-redux-toastr";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";

import { TAG_INPUTS } from "./seedInputOptions";

import { stripPhoneNumber, readablePhoneNumber } from "../../../utils/normalize";

const SeedModal = ({ isOpen, selected, toggleModal, applyChanges }) => {
  const [newSeedList, setNewSeedList] = useState({
    seed_list_id_bint: selected.seed_list_id_bint,
    name_vch: selected.name_vch || "",
    email_list_txt: transformStringToTags(selected.email_list_txt, "email"),
    text_list_txt: transformStringToTags(selected.text_list_txt, "phone"),
    voice_list_txt: transformStringToTags(selected.voice_list_txt, "phone"),
  });

  function transformStringToTags(list, type) {
    if (!list || list.length === 0) {
      return [];
    }

    //when coming back after adding new list, list param is in the array-of-object format
    if (Array.isArray(list)) {
      return jasonArrayToPropString(list)
        .split(",")
        .map((contact) => ({
          value: type === "phone" ? stripPhoneNumber(contact) : contact,
          name: type === "phone" ? readablePhoneNumber(contact) : contact,
        }));
    } else {
      return list.split(",").map((contact) => ({
        value: type === "phone" ? stripPhoneNumber(contact) : contact,
        name: type === "phone" ? readablePhoneNumber(contact) : contact,
      }));
    }
  }

  function jasonArrayToPropString(someJsonArray) {
    return Array.prototype.map.call(someJsonArray, (s) => s.name).toString();
  }

  const closeBtn = (
    <button className="close" onClick={toggleModal}>
      &times;
    </button>
  );

  function updateSeedList({ name, value }) {
    if (/name/.test(name)) {
      value = value.replace(/[^a-z0-9-/'.\s]/i, "");
    }

    setNewSeedList({
      ...newSeedList,
      [name]: value,
    });
  }

  function validateTag(key, type, tag) {
    // Check for duplicate
    if (newSeedList[key].find((contact) => contact.value === tag.name)) {
      toastr.light("Looks like that contact is already in the list", {
        icon: "warning",
        status: "warning",
      });
      return false;
    }

    if (type === "phone" && !/^\d{10,11}$/.test(tag.name.replace(/[^\d]/g, ""))) {
      toastr.light(`Please enter a valid phone number: ${readablePhoneNumber(tag.name)}`, {
        icon: "warning",
        status: "warning",
      });
      return false;
    }

    if (type === "email" && !/.+@.+/.test(tag.name)) {
      toastr.error("Please enter a valid email address");
      return false;
    }

    return true;
  }

  function handleAddition(key, type, tag) {
    if (type === "phone") {
      tag.value = tag.name;
      tag.name = readablePhoneNumber(tag.name);
    }

    newSeedList[key] = newSeedList[key].concat(tag);
    setNewSeedList({ ...newSeedList });
  }
  function handleDelete(type, index) {
    newSeedList[type].splice(index, 1);
    setNewSeedList({ ...newSeedList });
  }

  function validateChanges(e) {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    if (!newSeedList.name_vch) {
      return toastr.error("Oops", "Please enter a List name");
    }
    if (!newSeedList.email_list_txt && !newSeedList.text_list_txt && !newSeedList.voice_list_txt) {
      return toastr.error("Oops", "Please enter at least one contact method: Email, SMS, Phone");
    }

    // Don't overwrite state
    const _newSeedList = { ...newSeedList };

    ["email_list_txt", "text_list_txt", "voice_list_txt"].forEach((type) => {
      _newSeedList[type] = _newSeedList[type]
        .reduce((contacts, tag) => contacts.concat(tag.value), [])
        .join(",");
    });

    applyChanges(newSeedList).then((success) => {
      if (success) {
        toggleModal(false);
      }
    });
  }

  const TagButton = ({ tag, onDelete }) => (
    <Button size="sm" onClick={onDelete} className="m-1">
      {tag.name} | <FontAwesomeIcon icon={faTimes} size="lg" />
    </Button>
  );

  return (
    <Modal isOpen={isOpen} toggle={toggleModal}>
      <ModalHeader toggle={toggleModal} close={closeBtn}>
        {newSeedList.seed_list_id_bint ? "Edit" : "Create"} Seed List
      </ModalHeader>

      <ModalBody>
        <Row>
          <Col>
            <p>
              Numbers in the input will not be saved.
              <br />
              Press "tab" or "enter" to accept an input.
              <br />
              Tags will appear as <TagButton tag={{ name: "1 (222) 333-4444" }} /> or{" "}
              <TagButton tag={{ name: "sample@email.com" }} /> when accepted.
            </p>
          </Col>
        </Row>

        <hr />

        <Row className="mb-4">
          <Col className="col-12 col-md-4">
            <h4>
              List Name <span className="text-danger">*</span>
            </h4>
          </Col>

          <Col className="col-12 col-md-8">
            <Input
              name="name_vch"
              value={newSeedList.name_vch}
              onChange={({ target }) => updateSeedList(target)}
            />
          </Col>
        </Row>

        {TAG_INPUTS.map(({ label, param, placeholder, type }) => (
          <Row key={param} className="mb-4">
            <Col className="col-12 col-md-4">
              <h4>
                {label} {TAG_INPUTS.length === 1 && <span className="text-danger">*</span>}
              </h4>
            </Col>

            <Col className="col-12 col-md-8">
              <ReactTags
                allowNew={true}
                allowBackspace={false}
                tags={newSeedList[param]}
                placeholder={placeholder}
                inputAttributes={{ className: "form-control w-100" }}
                handleValidate={(tag) => validateTag(param, type, tag)}
                handleAddition={(tag) => handleAddition(param, type, tag)}
                handleDelete={(index) => handleDelete(param, index)}
                tagComponent={TagButton}
              />
            </Col>
          </Row>
        ))}

        <Row>
          <Col className="text-right">
            <small>
              Note: <span className="text-danger">*</span> denotes a required field
            </small>
          </Col>
        </Row>
      </ModalBody>

      <ModalFooter className="d-flex justify-content-between">
        <Button color="secondary" onClick={toggleModal}>
          Cancel
        </Button>
        <Button color="primary" onClick={validateChanges}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SeedModal;
